import React, { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import PageWrapper from './components/PageWrapper';
import FullWidthPageWrapper from './components/FullWidthPageWrapper';
import Loader from './components/Loader';

// Layouts (keep these eager as they're needed immediately)
import AuthLayout from './layouts/Auth';
import DashboardLayout from './layouts/Dashboard';

// Guards (keep these eager as they're needed immediately)
import AuthGuard from './components/guards/AuthGuard';
import AdminGuard from './components/guards/AdminGuard';
import SalespersonGuard from './components/guards/SalespersonGuard';
import PartnerGuard from './components/guards/PartnerGuard';

// Contexts (keep these eager)
import { LeadsContextDashboardProvider } from './contexts/dashboard/LeadsDashboardContext';

// Auth components (lazy load - not needed on initial page load)
const SignIn = lazy(() => import('./pages/auth/SignIn'));
const RestorePassword = lazy(() => import('./pages/auth/RestorePassword'));
const NewPassword = lazy(() => import('./pages/auth/NewPassword'));
const ConfirmInvite = lazy(() => import('./pages/auth/ConfirmInvite'));
const Page404 = lazy(() => import('./pages/auth/Page404'));

// Protected routes (lazy load - only loaded after authentication)
const UsersList = lazy(() => import('./components/AdminPage/UsersList'));
const SitesList = lazy(() => import('./components/AdminPage/SitesList'));
const HemsidorList = lazy(() => import('./components/AdminPage/HemsidorList'));
const DomainPage = lazy(() => import('./components/UserPage/DomainPage'));
// const LeadsPage = lazy(() => import('./components/UserPage/LeadsPage'));
const ClientLeadManagement = lazy(() => import('./components/ClientPage/ClientLeadManagement'));
const LeadsList = lazy(() => import('./components/AdminPage/LeadsList'));
const DashboardPage = lazy(() => import('./components/UserPage/DashboardPage'));
const AdminDashboardPage = lazy(() => import('./components/AdminPage/AdminDashboardPage'));
const RankingPage = lazy(() => import('./components/AdminPage/RankingPage'));
const UserStatisticsPage = lazy(() => import('./components/AdminPage/UserStatisticsPage'));
const UserSettingsPage = lazy(() => import('./components/UserPage/UserSettingsPage'));
const Sales = lazy(() => import('./components/AdminPage/SalesPage'));
const RankingDashboardPage = lazy(() => import('./components/SalespersonPage/RankingDashboardPage'));
const ClientHemsidorList = lazy(() => import('./components/ClientPage/ClientHemsidorList'));
const AdminClientList = lazy(() => import('./components/AdminPage/AdminClientList'));
const AssignSiteOwner = lazy(() => import('./components/AdminPage/AssignSiteOwner'));
const InfoPage = lazy(() => import('./components/AdminPage/InfoPage'));
const InfoForm = lazy(() => import('./components/AdminPage/InfoForm'));
// const LeadPage = lazy(() => import('./components/UserPage/LeadPage'));
const OptimizationClientList = lazy(() => import('./components/AdminPage/OptimizationClientList'));
const ClientsCompaniesList = lazy(() => import('./components/AdminPage/ClientsCompaniesList'));
const PromotionsListContainer = lazy(() => import('./containers/proposals/ProposalsListContainer'));
const ProposalPage = lazy(() => import('./containers/proposals/ProposalPage'));
const AdminInvoicesPage = lazy(() => import('./components/AdminPage/AdminInvoicesPage'));
const ClientInvoicePage = lazy(() => import('./components/ClientInvoicePage'));
const LeadExpectationPage = lazy(() => import('./components/AdminPage/LeadsDashboard/LeadExpectationPage'));
const LeadDetailsPage = lazy(() => import('./components/AdminPage/LeadManagement/LeadDetailsPage'));
const DpaAgreementPage = lazy(() => import('./components/ClientPage/DpaAgreementPage'));
const DpaAdminPage = lazy(() => import('./components/AdminPage/DpaAdminPage'));

// Analytics Components (lazy load)
const AdminAnalyticsPage = lazy(() => import('./components/Analytics/AdminAnalyticsPage'));
const ClientAnalyticsPage = lazy(() => import('./components/Analytics/ClientAnalyticsPage'));
const SalespersonAnalyticsPage = lazy(() => import('./components/Analytics/SalespersonAnalyticsPage'));

// Admin Data Management Components (lazy load)
const CitiesList = lazy(() => import('./components/AdminPage/CitiesList'));
const BlocklistManagement = lazy(() => import('./components/AdminPage/BlocklistManagement'));
const UtmOverviewPage = lazy(() => import('./components/AdminPage/UtmOverview/UtmOverviewPage'));
const OurLeadsPage = lazy(() => import('./components/AdminPage/OurLeads/OurLeadsPage'));
const OurLeadDetailsPage = lazy(() => import('./components/AdminPage/OurLeads/OurLeadDetailsPage'));

// Wrapper component for lazy loaded routes with Suspense
const LazyComponent = ({ children }) => (
  <Suspense fallback={<Loader />}>
    {children}
  </Suspense>
);

const routes = [
  {
    path: '/',
    element: <Navigate to="/auth/sign-in" />,
  },
  {
    path: 'auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'sign-in',
        element: (
          <LazyComponent>
            <PageWrapper title="Sign In">
              <SignIn />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'restore-password',
        element: (
          <LazyComponent>
            <PageWrapper title="Restore Password">
              <RestorePassword />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'restore-new-password',
        element: (
          <LazyComponent>
            <PageWrapper title="New Password">
              <NewPassword />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'invite-confirmation',
        element: (
          <LazyComponent>
            <PageWrapper title="Confirm Invite">
              <ConfirmInvite />
            </PageWrapper>
          </LazyComponent>
        ),
      },
    ],
  },
  {
    path: 'client',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { path: '/client', element: <Navigate to="/client/dashboard" /> },
      {
        path: 'dashboard',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Dashboard">
              <DashboardPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'pages',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Landningssidor">
              <DomainPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'hemsidor',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Hemsidor">
              <ClientHemsidorList />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'invoices',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Fakturor page">
              <ClientInvoicePage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper logo="true" title="Kundförfrågningar">
              <ClientLeadManagement />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <LeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Info">
              <UserSettingsPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'avtal',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Avtal">
              <DpaAgreementPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'statistik',
        children: [
          {
            path: 'hemsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Hemsidor">
                  <ClientAnalyticsPage siteType="brandpages" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'info',
        children: [
          {
            path: 'upload',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Upload images">
                  <InfoPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'form',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Form">
                  <InfoForm />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
    ],
  },
  {
    path: 'salesperson',
    element: (
      <AuthGuard>
        <SalespersonGuard>
          <DashboardLayout />
        </SalespersonGuard>
      </AuthGuard>
    ),
    children: [
      { path: '/salesperson', element: <Navigate to="/salesperson/dashboard/default" /> },
      {
        path: 'dashboard',
        children: [
          {
            path: 'default',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Default">
                  <AdminDashboardPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'sales',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Sälj">
                  <Sales />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'ranking',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Ranking">
                  <RankingDashboardPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'user-statistics',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="User statistics">
                  <UserStatisticsPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'statistik',
        children: [
          {
            path: 'landningsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Landningssidor">
                  <SalespersonAnalyticsPage siteType="sites" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'hemsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Hemsidor">
                  <SalespersonAnalyticsPage siteType="brandpages" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'sites',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Landningssidor">
              <DomainPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'hemsidor',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Hemsidor">
              <HemsidorList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Inlogg kunder">
              <AdminClientList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Inlogg kunder">
              <AssignSiteOwner />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'kundföretag',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundföretag">
              <ClientsCompaniesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Inställningar">
              <UserSettingsPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundförfrågningar">
              <LeadsList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <LeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'users',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Användare">
              <UsersList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'users/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Användare">
              <AssignSiteOwner />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'proposal',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Proposals">
              <PromotionsListContainer />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'proposal/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Proposals page">
              <ProposalPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'invoices',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Fakturor page">
              <AdminInvoicesPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'data-management',
        children: [
          {
            path: 'cities',
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Städer Hantering">
                  <CitiesList />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
    ],
  },
  {
    path: 'admin',
    element: (
      <AuthGuard>
        <AdminGuard>
          <DashboardLayout />
        </AdminGuard>
      </AuthGuard>
    ),
    children: [
      { path: '/admin', element: <Navigate to="/admin/dashboard/default" /> },
      {
        path: 'dashboard',
        children: [
          {
            path: 'default',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Default">
                  <AdminDashboardPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'sales',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Sälj">
                  <Sales />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'ranking',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Ranking">
                  <RankingPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'user-statistics',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="User statistics">
                  <UserStatisticsPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'leads',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Leads">
                  <LeadsContextDashboardProvider>
                    <LeadExpectationPage />
                  </LeadsContextDashboardProvider>
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'statistik',
        children: [
          {
            path: 'landningsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Landningssidor">
                  <AdminAnalyticsPage siteType="sites" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'hemsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Hemsidor">
                  <AdminAnalyticsPage siteType="brandpages" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'sites',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Landningssidor">
              <SitesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'hemsidor',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Hemsidor">
              <HemsidorList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Inlogg kunder">
              <AdminClientList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Inlogg kunder">
              <AssignSiteOwner />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'kundföretag',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundföretag">
              <ClientsCompaniesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'our-leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Våra leads">
              <OurLeadsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'our-leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <OurLeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundförfrågningar">
              <LeadsList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <LeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Inställningar">
              <UserSettingsPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'users',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Användare">
              <UsersList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'users/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Användare">
              <AssignSiteOwner />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'optimering',
        children: [
          {
            path: ':status',
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Optimering">
                  <OptimizationClientList />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'proposal',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Proposals">
              <PromotionsListContainer />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'proposal/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Proposals page">
              <ProposalPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'invoices',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Fakturor page">
              <AdminInvoicesPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'data-management',
        children: [
          {
            path: 'cities',
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Städer Hantering">
                  <CitiesList />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'blocklist',
            element: (
              <LazyComponent>
                <FullWidthPageWrapper title="Blocklista Hantering">
                  <BlocklistManagement />
                </FullWidthPageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'utm-overview',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="UTM Spårning">
              <UtmOverviewPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'dpa',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="DPA Avtal">
              <DpaAdminPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
    ],
  },
  {
    path: 'partner',
    element: (
      <AuthGuard>
        <PartnerGuard>
          <DashboardLayout />
        </PartnerGuard>
      </AuthGuard>
    ),
    children: [
      { path: '/partner', element: <Navigate to="/partner/leads" /> },
      {
        path: 'sites',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Landningssidor">
              <SitesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'hemsidor',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Hemsidor">
              <HemsidorList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundförfrågningar">
              <LeadsList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <LeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Inställningar">
              <UserSettingsPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
    ],
  },
  {
    path: 'user',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { path: '/user', element: <Navigate to="/user/dashboard/default" /> },
      {
        path: 'dashboard',
        children: [
          {
            path: 'default',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Default">
                  <AdminDashboardPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'ranking',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Ranking">
                  <RankingPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'user-statistics',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="User statistics">
                  <UserStatisticsPage />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'statistik',
        children: [
          {
            path: 'landningsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Landningssidor">
                  <AdminAnalyticsPage siteType="sites" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
          {
            path: 'hemsida',
            element: (
              <LazyComponent>
                <PageWrapper logo="true" title="Statistik - Hemsidor">
                  <AdminAnalyticsPage siteType="brandpages" />
                </PageWrapper>
              </LazyComponent>
            ),
          },
        ],
      },
      {
        path: 'sites',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Landningssidor">
              <SitesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'hemsidor',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Hemsidor">
              <HemsidorList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Inlogg kunder">
              <AdminClientList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'client/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Inlogg kunder">
              <AssignSiteOwner />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'kundföretag',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundföretag">
              <ClientsCompaniesList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Kundförfrågningar">
              <LeadsList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'leads/:id',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Lead Detaljer">
              <LeadDetailsPage />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Inställningar">
              <UserSettingsPage />
            </PageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'users',
        element: (
          <LazyComponent>
            <FullWidthPageWrapper title="Användare">
              <UsersList />
            </FullWidthPageWrapper>
          </LazyComponent>
        ),
      },
      {
        path: 'users/:id',
        element: (
          <LazyComponent>
            <PageWrapper logo="true" title="Användare">
              <AssignSiteOwner />
            </PageWrapper>
          </LazyComponent>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <AuthLayout />,
    children: [
      {
        path: '*',
        element: (
          <LazyComponent>
            <PageWrapper title="404">
              <Page404 />
            </PageWrapper>
          </LazyComponent>
        ),
      },
    ],
  },
];

export default routes;
